import { HGInterval, HGIntervalType } from "./types";
import { GetSpans } from "@/engine/engine";
import { getMaxN } from "./intervalTypes";

/**
 * The main chain, coarse -> fine.
 *
 * `dec` is here although FIT_CHAIN (`fitRange.ts`) leaves it out: it is the
 * only landing between `c` and `y`, and that 100x jump is exactly where a
 * century's worth of records overflows the cap below. It is reachable by
 * `HGStack.setInterval()` — `c` lists it in `childrenTypes`, so the backwards
 * search through the parent chain finds it from any stack below century level.
 *
 * `w` and the fraction types are left out: they straddle their parents, so
 * neither their names nor their boundaries read as a subdivision of the
 * interval the user is looking at.
 */
const BUCKET_CHAIN: HGIntervalType[] = [
  "byr",
  "myr",
  "kyr",
  "c",
  "dec",
  "y",
  "m",
  "d",
  "h",
  "min",
  "s",
];

/**
 * How many buttons the empty state may offer. Past this the row stops being
 * scannable and the answer "here is where your records are" turns back into
 * "here is your whole collection".
 */
export const MAX_JUMP_BUCKETS = 12;

/**
 * Names get much longer as the level gets finer — "1789" against
 * "3 January 1789, Saturday" — so fewer of them fit before the row stops
 * reading as a row. This is also what keeps a dozen day-dated records
 * answering "1789, 1792, 1799" rather than listing themselves back.
 */
const MAX_BUCKETS_BY_LEVEL: Record<HGIntervalType, number> = {
  m: 8,
  d: 6,
  h: 6,
  min: 6,
  s: 6,
};

function maxBuckets(it: HGIntervalType): number {
  return MAX_BUCKETS_BY_LEVEL[it] ?? MAX_JUMP_BUCKETS;
}

/** One [start, end] second pair and how many records share it. */
export interface RecordSpan {
  start: bigint;
  end: bigint;
  count: number;
}

export interface IntervalBucket {
  interval: HGInterval;
  count: number;
}

/**
 * Every level's bucket number for one second, in one crossing into the engine.
 *
 * `GetSpans` takes a list of target types, so asking for the whole chain at
 * once costs what asking for a single level costs — eleven `ConvertInterval`
 * calls would cost eleven times as much.
 */
function bucketNumbers(second: bigint): Record<HGIntervalType, bigint> {
  const spans = GetSpans({ n: second, it: "s" }, null, BUCKET_CHAIN);
  const numbers: Record<HGIntervalType, bigint> = {};
  // A type the engine has no mapping for is simply absent; the walk skips it
  // rather than treating the gap as an answer.
  for (const it of BUCKET_CHAIN) {
    const span = spans[it];
    if (span) numbers[it] = span.begin.n;
  }
  return numbers;
}

function clampToType(n: bigint, it: HGIntervalType): bigint {
  const maxN = getMaxN(it);
  if (maxN === undefined) return n;
  if (n < -maxN) return -maxN;
  if (n > maxN) return maxN;
  return n;
}

/**
 * The non-empty buckets at one level, or null if the level is unusable.
 *
 * A span that lands inside a single bucket **creates** that bucket; one that
 * straddles several only **joins** the buckets already there. That asymmetry
 * is what keeps the level from running finer than the records themselves: a
 * record entered as the year 1789 covers all twelve months of 1789, so at
 * month level it creates nothing, and a level where nothing was created is
 * rejected. Level is also rejected when a straddling span joins nothing —
 * that record would have no button at all, and a signpost that omits a
 * destination is worse than no signpost.
 */
function bucketAt(
  spans: RecordSpan[],
  starts: Record<HGIntervalType, bigint>[],
  ends: Record<HGIntervalType, bigint>[],
  it: HGIntervalType,
): IntervalBucket[] | null {
  const buckets = new Map<string, IntervalBucket>();
  const wide: number[] = [];
  const limit = maxBuckets(it);

  for (let i = 0; i < spans.length; ++i) {
    const from = clampToType(starts[i][it], it);
    const to = clampToType(ends[i][it], it);
    if (from !== to) {
      wide.push(i);
      continue;
    }
    const key = from.toString();
    const bucket = buckets.get(key);
    if (bucket) bucket.count += spans[i].count;
    else buckets.set(key, { interval: { n: from, it }, count: spans[i].count });
    if (buckets.size > limit) return null;
  }

  if (buckets.size === 0) return null;

  // Walk the buckets per straddling span rather than the span's own range:
  // there is a handful of them, while the range can be billions of ticks long.
  for (const i of wide) {
    const from = clampToType(starts[i][it], it);
    const to = clampToType(ends[i][it], it);
    let joined = 0;
    for (const bucket of buckets.values()) {
      if (bucket.interval.n >= from && bucket.interval.n <= to) {
        bucket.count += spans[i].count;
        joined++;
      }
    }
    if (joined === 0) return null;
  }

  return [...buckets.values()].sort((a, b) =>
    a.interval.n < b.interval.n ? -1 : 1,
  );
}

/**
 * Where in time a set of records sits, expressed as a handful of intervals to
 * jump to.
 *
 * The level is **the finest one that works** — see {@link bucketAt} for what
 * disqualifies a level. Finest, because the whole point is to be more specific
 * than "somewhere in this collection"; and because the disqualifying rules
 * already stop the walk from running past the precision the records were
 * entered at, a set of year-dated records yields years, a set of day-dated
 * ones yields days, and forty years that will not fit in twelve buttons fall
 * back to decades on their own.
 *
 * Bucket counts are not monotonic along the chain — going finer can turn every
 * span into a straddling one and empty the level out — so every level is
 * evaluated rather than stopping at the first failure.
 */
export function bucketRecordSpans(spans: RecordSpan[]): IntervalBucket[] {
  if (spans.length === 0) return [];

  const starts = spans.map((span) => bucketNumbers(span.start));
  const ends = spans.map((span) => bucketNumbers(span.end));
  const chain = BUCKET_CHAIN.filter((it) => it in starts[0]);

  let best: IntervalBucket[] = [];
  for (const it of chain) {
    const buckets = bucketAt(spans, starts, ends, it);
    if (buckets) best = buckets;
  }

  return best;
}
